import { type DocumentResource } from "@/lib/document-resource";
import { openDocumentResource } from "@/stores/document-preview-store";
import { DocumentThumbnail } from "@/components/documents/DocumentThumbnail";
import { DocumentOpenButton } from "@/components/documents/DocumentOpenButton";
import { userErrorMessage } from "@/lib/user-error";
import { useState, type KeyboardEvent, type ReactNode } from "react";
import { useIntl } from "react-intl";

// Canonical preview tile for an uploaded/attached file (@adr SPARK-0022-file-drop-fields@4). One look for
// in-memory upload previews across the app (Match challenge, Share create,
// extraction): a square card with an image thumbnail when available, otherwise
// a neutral file glyph, plus name + size, an optional corner badge, and a
// hover remove button. (ServicePage's DocPreviewCard is the richer per-document
// card backed by genesis-generated thumbnails; this tile is for fresh uploads.)

function formatSize(bytes?: number | null): string {
  if (!bytes) return "";
  const kb = Math.round(bytes / 1024);
  return kb < 1024 ? `${kb} KB` : `${(kb / 1024).toFixed(1)} MB`;
}

export function DocumentTile({
  file,
  footer,
  idPrefix,
  name,
  sizeBytes,
  previewUrl,
  badge,
  selected = false,
  onRemove,
  onClick,
  title,
}: {
  file: DocumentResource;
  footer?: ReactNode;
  /** Testanker; unbesetzt, wo kein Test hingreift. */
  idPrefix?: string;
  name: string;
  sizeBytes?: number | null;
  /** Image thumbnail URL; falls back to a file glyph when absent. */
  previewUrl?: string | null;
  /** Corner badge, e.g. a "preview image" marker. */
  badge?: ReactNode;
  selected?: boolean;
  onRemove?: () => void;
  onClick?: () => void;
  title?: string;
}) {
  const intl = useIntl();
  const size = formatSize(sizeBytes);
  const resource = file;
  const [error, setError] = useState<unknown>();
  const open = () => { if (resource) { setError(undefined); void openDocumentResource(resource).catch(setError); } };
  const activate = onClick ?? (!resource.unavailableReason ? open : undefined);
  const previewLabel = resource?.previewable ? intl.formatMessage({ id: "documents.preview.open" }) : undefined;
  const interactiveProps = activate
    ? {
        role: "button" as const,
        tabIndex: 0,
        "aria-label": title ?? name,
        "aria-pressed": selected,
        onKeyDown: (e: KeyboardEvent<HTMLDivElement>) => {
          if (e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            activate();
          }
        },
      }
    : {};
  return (
    <div
      onClick={activate}
      title={title ?? previewLabel}
      {...interactiveProps}
      data-state-mark-position="start"
      className={`bg-page box-flat group relative overflow-hidden transition ${
        selected ? "smycles-selected ring-2 ring-accent-decoration" : "ring-1 ring-ink/5"
      } ${activate ? "cursor-pointer hover:ring-accent-decoration/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink-accent-ui" : ""}`}
    >
      {onRemove ? (
        <button
          id={idPrefix && `${idPrefix}-remove`}
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          title={intl.formatMessage({ id: "common.remove", defaultMessage: "Remove" })}
          aria-label={intl.formatMessage({ id: "common.remove", defaultMessage: "Remove" })}
          className="absolute right-1.5 top-1.5 z-10 flex h-5 w-5 items-center justify-center rounded-full bg-ink text-smycles-micro text-ink opacity-0 transition group-hover:opacity-100 focus:opacity-100 focus-visible:opacity-100"
        >
          ✕
        </button>
      ) : null}
      <div className="relative aspect-[4/3] overflow-hidden bg-ink/[0.06]">
        <span className="absolute inset-0"><DocumentThumbnail resource={resource} previewSrc={previewUrl} fallback={<span className="text-smycles-display-s" aria-hidden>📄</span>} /></span>
      </div>
      {badge ? <div className="absolute bottom-1 left-1">{badge}</div> : null}
      <div className="px-2 py-1.5">
        <div id={idPrefix && `${idPrefix}-name`} className="truncate text-smycles-label font-medium">{name}</div>
        <div className="flex items-center justify-between gap-1">
          {size ? <span className="text-smycles-micro text-ink-muted">{size}</span> : <span />}
          {resource && <span onClick={e => e.stopPropagation()}><DocumentOpenButton id={idPrefix && `${idPrefix}-open`} name={name} resource={resource} onOpen={open} /></span>}
        </div>
        {footer && <div onClick={event => event.stopPropagation()}>{footer}</div>}
        {error ? <p role="alert" className="text-smycles-micro">{userErrorMessage(intl, error)}</p> : null}
      </div>
    </div>
  );
}
